/**
 * 보스 사망 이펙트
 * 여러 개의 작은 원이 교차로 나타나서 터지는 효과
 * 각 원: 작음->커짐 (채워진 상태) -> 외곽선만 -> 사라짐
 */

export interface DeathEffectCircle {
    x: number;
    y: number;
    targetRadius: number;
    currentRadius: number;
    phase: 'growing' | 'outline' | 'fading';
    alpha: number;
    delay: number;
    elapsed: number;
}

export class BossDeathEffect {
    private circles: DeathEffectCircle[] = [];
    private centerX: number;
    private centerY: number;
    private isActive: boolean = true;
    private totalDuration: number = 0.8; // 전체 효과 시간
    private elapsed: number = 0;

    // 외부에서 위치 변환을 위한 getter/setter
    get x(): number { return this.centerX; }
    set x(value: number) {
        const deltaX = value - this.centerX;
        this.centerX = value;
        for (const circle of this.circles) {
            circle.x += deltaX;
        }
    }

    get y(): number { return this.centerY; }
    set y(value: number) {
        const deltaY = value - this.centerY;
        this.centerY = value;
        for (const circle of this.circles) {
            circle.y += deltaY;
        }
    }

    constructor(x: number, y: number, bossRadius: number = 40) {
        this.centerX = x;
        this.centerY = y;

        // 여러 원 생성 (교차 패턴)
        const circleCount = 6;
        for (let i = 0; i < circleCount; i++) {
            const angle = (i / circleCount) * Math.PI * 2;
            const distance = bossRadius * 0.5;

            this.circles.push({
                x: x + Math.cos(angle) * distance,
                y: y + Math.sin(angle) * distance,
                targetRadius: bossRadius * (0.4 + Math.random() * 0.3),
                currentRadius: 0,
                phase: 'growing',
                alpha: 1,
                delay: i * 0.03, // 순차적 시작
                elapsed: 0
            });
        }

        // 중앙에 큰 원 추가
        this.circles.push({
            x: x,
            y: y,
            targetRadius: bossRadius * 0.8,
            currentRadius: 0,
            phase: 'growing',
            alpha: 1,
            delay: 0.1,
            elapsed: 0
        });
    }

    update(deltaTime: number): void {
        if (!this.isActive) return;

        this.elapsed += deltaTime;

        const growDuration = 0.15;
        const outlineDuration = 0.2;
        const fadeDuration = 0.15;

        for (const circle of this.circles) {
            // 딜레이 처리
            if (this.elapsed < circle.delay) continue;

            circle.elapsed += deltaTime;
            const t = circle.elapsed;

            if (circle.phase === 'growing') {
                // 커지는 단계
                const progress = Math.min(t / growDuration, 1);
                circle.currentRadius = circle.targetRadius * this.easeOutQuad(progress);

                if (progress >= 1) {
                    circle.phase = 'outline';
                    circle.elapsed = 0;
                }
            } else if (circle.phase === 'outline') {
                // 외곽선만 남는 단계
                if (circle.elapsed >= outlineDuration) {
                    circle.phase = 'fading';
                    circle.elapsed = 0;
                }
            } else if (circle.phase === 'fading') {
                // 사라지는 단계
                const progress = Math.min(circle.elapsed / fadeDuration, 1);
                circle.alpha = 1 - progress;
                circle.currentRadius = circle.targetRadius * (1 + progress * 0.3);
            }
        }

        // 모든 원이 사라지면 비활성화
        if (this.elapsed >= this.totalDuration) {
            this.isActive = false;
        }
    }

    private easeOutQuad(t: number): number {
        return 1 - (1 - t) * (1 - t);
    }

    render(ctx: CanvasRenderingContext2D): void {
        if (!this.isActive) return;

        ctx.save();

        for (const circle of this.circles) {
            if (circle.currentRadius <= 0 || circle.alpha <= 0) continue;
            if (this.elapsed < circle.delay) continue;

            ctx.beginPath();
            ctx.arc(circle.x, circle.y, circle.currentRadius, 0, Math.PI * 2);

            if (circle.phase === 'growing') {
                // 채워진 상태
                ctx.fillStyle = `rgba(255, 255, 255, ${circle.alpha * 0.6})`;
                ctx.fill();
                ctx.strokeStyle = `rgba(255, 255, 255, ${circle.alpha})`;
                ctx.lineWidth = 2;
                ctx.stroke();
            } else {
                // 외곽선만
                ctx.strokeStyle = `rgba(255, 255, 255, ${circle.alpha})`;
                ctx.lineWidth = 2;
                ctx.stroke();
            }
        }

        ctx.restore();
    }

    getIsActive(): boolean {
        return this.isActive;
    }

    getElapsed(): number {
        return this.elapsed;
    }

    getCircles(): DeathEffectCircle[] {
        return this.circles;
    }
}
